/**
 * publicSessionLinkService.js : routes publiques du lien de séance (D243, D247).
 *
 * Le jeton vient du fragment de l'adresse (`/seance-partagee#<jeton>`), que le
 * navigateur n'envoie jamais au serveur ; il voyage dans l'en-tête
 * X-Session-Link-Token, jamais dans un chemin ni une query string. Aucun
 * cookie (`credentials: 'omit'`) : la page n'est liée à aucun compte, même si
 * le visiteur en a un. Le jeton n'est écrit nulle part (ni stockage du
 * navigateur, ni journal).
 */
import { fetchAllReadings } from '../fetchAllReadings';

export const SESSION_LINK_PAGE_PATH = '/seance-partagee';
const BASE = '/api/public/session-link';
const TOKEN_HEADER = 'X-Session-Link-Token';
// Format du jeton (backend/utils/sessionLinkRules.js : 32 octets en base64url).
const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/** Jeton lu dans un fragment d'adresse (`#<jeton>`) ; null s'il est mal formé. */
export function readTokenFromHash(hash) {
  const token = String(hash || '').replace(/^#/, '');
  return TOKEN_RE.test(token) ? token : null;
}

const requestInit = (token) => ({ credentials: 'omit', headers: { [TOKEN_HEADER]: token } });

/**
 * Erreur à code de la route (LINK_NOT_FOUND, LINK_EXPIRED, LINK_REVOKED, TOO_MANY_REQUESTS, SERVER_ERROR).
 * Partagée avec le lien de comparaison (D257).
 */
export async function linkError(res) {
  let code = null;
  try {
    code = (await res.json())?.code || null;
  } catch (err) {
    // Corps illisible (proxy, page d'erreur) : code déduit du statut ci-dessous.
    code = null;
  }
  if (!code) code = res.status === 429 ? 'TOO_MANY_REQUESTS' : res.status === 404 ? 'LINK_NOT_FOUND' : 'SERVER_ERROR';
  return Object.assign(new Error(code), { code, status: res.status });
}

async function getJson(url, token, signal) {
  const res = await fetch(url, { ...requestInit(token), signal });
  if (!res.ok) throw await linkError(res);
  return res.json();
}

/** Séance, période et expiration du lien. */
export const fetchLinkedSession = (token, signal) => getJson(BASE, token, signal);

/** Modèles du résumé (FR), mêmes que la fiche du propriétaire. */
export async function fetchLinkedTemplates(token, signal) {
  const body = await getJson(`${BASE}/summary-templates`, token, signal);
  return body.templates;
}

/** Toutes les pages de mesures du lien (D235), bornées à sa période par le serveur. */
export async function fetchLinkedReadings(token, signal) {
  try {
    return await fetchAllReadings(null, {
      signal,
      pageUrl: (limit, skip) => `${BASE}/readings?limit=${limit}&skip=${skip}`,
      requestInit: requestInit(token),
    });
  } catch (err) {
    if (err?.response) throw await linkError(err.response);
    throw err;
  }
}
